// src/pages/AdminUserDetails.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { db, functions } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate, useParams } from "react-router-dom";

const COPPER = "#B87333";
const COMM_PAGE_SIZE = 10;

function normLower(v) {
  return String(v || "").trim().toLowerCase();
}

function formatDateTime(value) {
  if (!value) return "—";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }

    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleString();
    }

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString();
    }

    return "—";
  } catch {
    return "—";
  }
}

function formatDateOnly(value) {
  if (!value) return "—";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString();
    }

    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString();
    }

    return "—";
  } catch {
    return "—";
  }
}

function isDisabled(user) {
  const s = normLower(user?.status);
  return s === "disabled" || s === "suspended";
}

function getJoinedAt(user) {
  return user?.approvedAt || user?.joinedAt || user?.createdAt || null;
}

function initialsFromName(nameOrEmail) {
  const s = String(nameOrEmail || "").trim();
  if (!s) return "—";

  if (s.includes("@")) {
    const left = s.split("@")[0] || "";
    const a = (left[0] || "").toUpperCase();
    const b = (left[1] || "").toUpperCase();
    return `${a}${b}`.trim() || "—";
  }

  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  const out = (first + last).toUpperCase();
  return out || "—";
}

function getTimestampMs(value) {
  if (!value) return 0;

  try {
    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    if (typeof value?.seconds === "number") {
      return value.seconds * 1000;
    }

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.getTime();
    }

    return 0;
  } catch {
    return 0;
  }
}

function buildTournamentHistoryStatus(entrant) {
  if (!entrant) return "—";

  if (entrant.winner) return "Winner";
  if (Number(entrant.finishPlace || 0) > 0) return `Finished ${entrant.finishPlace}`;
  if (entrant.eliminated) return "Eliminated";
  return "Registered";
}

function prettifySource(source) {
  const s = normLower(source);

  if (!s) return "";
  if (s === "application_approval") return "Application Approval";
  if (s === "self_service") return "Self-Service";
  if (s === "admin_manual") return "Admin Manual";
  if (s === "tournament_create") return "Tournament Created";
  if (s === "tournament_update") return "Tournament Updated";
  if (s === "scheduled_early_bird_reminder") return "Scheduled Early Bird Reminder";
  if (s === "scheduled_tournament_reminder") return "Scheduled Tournament Reminder";
  if (s === "member_reply") return "Member Reply";

  return source;
}

function prettifyInboundAction(action) {
  const a = normLower(action);

  if (!a) return "Inbound SMS";
  if (a === "registered") return "Registered by SMS";
  if (a === "dealer_appreciation_prompted") return "REGISTER Reply";
  if (a === "registered_with_dealer_appreciation") {
    return "REGISTER + Dealer Appreciation";
  }
  if (a === "registered_without_dealer_appreciation") {
    return "REGISTER + Skip Dealer Appreciation";
  }

  return action;
}

function communicationTypeLabel(entry) {
  if (!entry) return "Communication";

  if (entry.channel === "email") {
    if (entry.type === "password_setup") return "Temporary Password Email";
    if (entry.type === "temporary_password") return "Temporary Password Email";
    if (entry.type === "announcement") return "Tournament Announcement Email";
    if (entry.type === "early_bird_reminder") return "Early Bird Reminder Email";
    if (entry.type === "tournament_reminder") return "Tournament Reminder Email";
    if (entry.type === "manual_reminder") return "Manual Reminder Email";
    return "Email";
  }

  if (entry.channel === "sms") {
    if (entry.type === "announcement") return "Tournament Announcement SMS";
    if (entry.type === "early_bird_reminder") return "Early Bird Reminder SMS";
    if (entry.type === "tournament_reminder") return "Tournament Reminder SMS";
    if (entry.type === "manual_reminder") return "Manual Reminder SMS";
    return "SMS";
  }

  if (entry.channel === "sms_inbound") {
    return `Inbound SMS — ${prettifyInboundAction(entry.action)}`;
  }

  return "Communication";
}

function getCommunicationStatusMeta(entry) {
  if (!entry) {
    return { text: "Sent", tone: "green" };
  }

  if (entry.channel === "sms_inbound") {
    return { text: "Received", tone: "blue" };
  }

  const failed = normLower(entry.status) === "failed" || !!entry.errorMessage;
  const queued = normLower(entry.status) === "queued";
  const deferred = normLower(entry.status) === "deferred";
  const delivered = normLower(entry.status) === "delivered";

  if (failed) return { text: "Failed", tone: "red" };
  if (deferred) return { text: "Deferred", tone: "amber" };
  if (queued) return { text: "Queued", tone: "amber" };
  if (delivered) return { text: "Delivered", tone: "green" };

  return { text: "Sent", tone: "green" };
}

function communicationDirectionLabel(entry) {
  return entry?.channel === "sms_inbound" ? "From" : "To";
}

function entryHeading(entry) {
  if (!entry) return "Communication";

  if (entry.channel === "email") {
    return entry.subject || communicationTypeLabel(entry);
  }

  return communicationTypeLabel(entry);
}

function normalizePhoneDigits(v) {
  return String(v || "").replace(/\D/g, "");
}

function buildPhoneQueryCandidates(phone) {
  const digits = normalizePhoneDigits(phone);
  const values = new Set();

  if (!digits) return [];

  values.add(digits);

  if (digits.length === 10) {
    values.add(`+1${digits}`);
    values.add(`1${digits}`);
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    values.add(`+${digits}`);
    values.add(digits.slice(1));
  }

  return Array.from(values).filter(Boolean);
}

async function safeQueryDocs(q, label) {
  try {
    const snap = await getDocs(q);
    return snap.docs;
  } catch (err) {
    console.error(`Failed query: ${label}`, err);
    return [];
  }
}

function Avatar({ user, size = 72 }) {
  const name = user?.name || user?.displayName || "";
  const email = user?.email || "";
  const photoUrl = user?.profilePhotoUrl || "";

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ? `${name} avatar` : "avatar"}
        className="rounded-full object-cover border border-zinc-700 bg-zinc-800"
        style={{ width: size, height: size }}
        loading="lazy"
      />
    );
  }

  const initials = initialsFromName(name || email);

  return (
    <div
      className="rounded-full flex items-center justify-center border bg-zinc-950"
      style={{
        width: size,
        height: size,
        borderColor: "rgba(184,115,51,0.25)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
        fontWeight: 800,
        letterSpacing: "0.02em",
        fontSize: 24,
      }}
    >
      {initials}
    </div>
  );
}

function DetailCard({ title, children, right }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-zinc-100">{title}</h3>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-zinc-800/70 last:border-b-0">
      <div className="text-zinc-500 text-sm">{label}</div>
      <div className="text-zinc-100 text-sm font-medium text-right break-all">
        {value || "—"}
      </div>
    </div>
  );
}

function Badge({ text, tone = "neutral" }) {
  const tones = {
    copper:
      "border-[rgba(184,115,51,0.30)] bg-[rgba(184,115,51,0.12)] text-[#B87333]",
    green: "border-green-500/30 bg-green-500/10 text-green-300",
    red: "border-red-500/30 bg-red-500/10 text-red-300",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-300",
    zinc: "border-zinc-700 bg-zinc-800/70 text-zinc-200",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-semibold ${
        tones[tone] || tones.zinc
      }`}
    >
      {text}
    </span>
  );
}

function TinyStat({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-semibold mt-1">{value}</div>
    </div>
  );
}

function FilterPill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
        active
          ? "text-zinc-950"
          : "text-zinc-300 bg-zinc-950 hover:bg-zinc-800"
      }`}
      style={
        active
          ? {
              background: COPPER,
              borderColor: COPPER,
            }
          : {
              borderColor: "rgba(184,115,51,0.20)",
            }
      }
    >
      {label}
    </button>
  );
}

export default function AdminUserDetails() {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [tournamentHistory, setTournamentHistory] = useState([]);
  const [communicationHistory, setCommunicationHistory] = useState([]);
  const [adminCount, setAdminCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadingRelated, setLoadingRelated] = useState(true);

  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [togglingEmailVerification, setTogglingEmailVerification] = useState(false);
  const [togglingPhoneVerification, setTogglingPhoneVerification] = useState(false);

  const [settingTemporaryPassword, setSettingTemporaryPassword] = useState(false);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [copyingTempPassword, setCopyingTempPassword] = useState(false);
  const [showStoredTempPassword, setShowStoredTempPassword] = useState(false);

  const [notesDraft, setNotesDraft] = useState("");
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [communicationFilter, setCommunicationFilter] = useState("all");
  const [visibleCommCount, setVisibleCommCount] = useState(COMM_PAGE_SIZE);

  const sendCustomPasswordSetupEmail = httpsCallable(
    functions,
    "sendCustomPasswordSetupEmail"
  );
  const adminUpdateUserProfile = httpsCallable(functions, "adminUpdateUserProfile");
  const adminSetTemporaryPassword = httpsCallable(
    functions,
    "adminSetTemporaryPassword"
  );

  const refreshUserDoc = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", memberId));
      if (userSnap.exists()) {
        setUser({
          id: userSnap.id,
          ...userSnap.data(),
        });
      }
    } catch (err) {
      console.error("Failed refreshing user doc:", err);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const [userSnap, adminsSnap] = await Promise.all([
          getDoc(doc(db, "users", memberId)),
          getDocs(query(collection(db, "users"), where("role", "==", "admin"))),
        ]);

        if (!userSnap.exists()) {
          setUser(null);
        } else {
          const nextUser = {
            id: userSnap.id,
            ...userSnap.data(),
          };
          setUser(nextUser);
          setNotesDraft(String(nextUser.adminNotes || ""));
          setProfileForm({
            name: String(nextUser.name || nextUser.displayName || ""),
            email: String(nextUser.email || ""),
            phone: String(nextUser.phone || ""),
          });
        }

        setAdminCount(adminsSnap.size);
      } catch (err) {
        console.error("Failed loading user details:", err);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [memberId]);

  useEffect(() => {
    const loadRelated = async () => {
      if (!user) {
        setLoadingRelated(false);
        return;
      }

      setLoadingRelated(true);

      try {
        let matchedApplication = null;

        const appQueries = [
          query(collection(db, "applications"), where("uid", "==", memberId), limit(1)),
        ];

        if (user.email) {
          appQueries.push(
            query(
              collection(db, "applications"),
              where("email", "==", String(user.email).trim().toLowerCase()),
              limit(1)
            )
          );

          appQueries.push(
            query(
              collection(db, "applications"),
              where("email", "==", String(user.email).trim()),
              limit(1)
            )
          );
        }

        for (const appQuery of appQueries) {
          const docs = await safeQueryDocs(appQuery, "applications");
          if (docs.length > 0) {
            matchedApplication = {
              id: docs[0].id,
              ...docs[0].data(),
            };
            break;
          }
        }

        setApplication(matchedApplication);

        const tournamentsSnap = await getDocs(collection(db, "tournaments"));

        const historyResults = await Promise.all(
          tournamentsSnap.docs.map(async (tournamentDoc) => {
            const tournament = {
              id: tournamentDoc.id,
              ...tournamentDoc.data(),
            };

            try {
              const entrantSnap = await getDoc(
                doc(db, "tournaments", tournament.id, "entrants", memberId)
              );

              if (!entrantSnap.exists()) return null;

              const entrant = entrantSnap.data() || {};

              return {
                tournamentId: tournament.id,
                tournamentName: tournament.name || "Tournament",
                tournamentDate: tournament.date || null,
                tournamentLocation: tournament.location || "",
                tournamentStatus: tournament.status || "",
                buyIn: tournament.buyIn || 0,
                entrant,
              };
            } catch (err) {
              console.error("Failed loading entrant history item:", err);
              return null;
            }
          })
        );

        const cleanedHistory = historyResults
          .filter(Boolean)
          .sort((a, b) => {
            const aDate = a?.tournamentDate || a?.entrant?.updatedAt || a?.entrant?.createdAt || null;
            const bDate = b?.tournamentDate || b?.entrant?.updatedAt || b?.entrant?.createdAt || null;
            return getTimestampMs(bDate) - getTimestampMs(aDate);
          });

        setTournamentHistory(cleanedHistory);

        const emailValue = String(user.email || "").trim();
        const phoneCandidates = buildPhoneQueryCandidates(user.phone);

        const emailLogQueries = [
          query(collection(db, "emailLogs"), where("memberUid", "==", memberId)),
        ];

        if (emailValue) {
          emailLogQueries.push(
            query(collection(db, "emailLogs"), where("to", "==", emailValue))
          );
          emailLogQueries.push(
            query(collection(db, "emailLogs"), where("to", "==", emailValue.toLowerCase()))
          );
        }

        const smsLogQueries = [
          query(collection(db, "smsLogs"), where("memberUid", "==", memberId)),
        ];

        phoneCandidates.forEach((candidate) => {
          smsLogQueries.push(
            query(collection(db, "smsLogs"), where("to", "==", candidate))
          );
        });

        const inboundSmsQueries = [
          query(collection(db, "smsInboundLogs"), where("memberUid", "==", memberId)),
        ];

        phoneCandidates.forEach((candidate) => {
          inboundSmsQueries.push(
            query(collection(db, "smsInboundLogs"), where("from", "==", candidate))
          );
        });

        const [emailDocsGroups, smsDocsGroups, inboundDocsGroups] = await Promise.all([
          Promise.all(emailLogQueries.map((q, idx) => safeQueryDocs(q, `emailLogs-${idx}`))),
          Promise.all(smsLogQueries.map((q, idx) => safeQueryDocs(q, `smsLogs-${idx}`))),
          Promise.all(
            inboundSmsQueries.map((q, idx) => safeQueryDocs(q, `smsInboundLogs-${idx}`))
          ),
        ]);

        const uniqueEmailDocs = new Map();
        emailDocsGroups.flat().forEach((d) => {
          uniqueEmailDocs.set(d.id, d);
        });

        const uniqueSmsDocs = new Map();
        smsDocsGroups.flat().forEach((d) => {
          uniqueSmsDocs.set(d.id, d);
        });

        const uniqueInboundDocs = new Map();
        inboundDocsGroups.flat().forEach((d) => {
          uniqueInboundDocs.set(d.id, d);
        });

        const emailEntries = Array.from(uniqueEmailDocs.values()).map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            channel: "email",
            type: data.emailType || "email",
            to: data.to || "",
            subject: data.subject || "",
            preview: data.previewText || data.messageBody || data.subject || "",
            status: data.status || (data.errorMessage ? "failed" : "sent"),
            errorMessage: data.errorMessage || "",
            createdAt: data.createdAt || null,
            tournamentId: data.tournamentId || null,
            requestedBySource: data.requestedBySource || "",
            requestedByName: data.requestedByName || "",
            provider: data.provider || "",
            providerMessageId: data.providerMessageId || "",
          };
        });

        const smsEntries = Array.from(uniqueSmsDocs.values()).map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            channel: "sms",
            type: data.smsType || "sms",
            to: data.to || "",
            subject: "",
            preview: data.previewText || data.messageBody || "",
            status: data.status || (data.errorMessage ? "failed" : "sent"),
            errorMessage: data.errorMessage || "",
            createdAt: data.createdAt || null,
            tournamentId: data.tournamentId || null,
            requestedBySource: data.requestedBySource || "",
            requestedByName: data.requestedByName || "",
            provider: data.provider || "twilio",
            providerMessageId: data.providerMessageId || data.twilioSid || "",
          };
        });

        const inboundSmsEntries = Array.from(uniqueInboundDocs.values()).map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            channel: "sms_inbound",
            type: "inbound_reply",
            to: data.from || "",
            subject: "",
            preview: data.body || data.messageBody || "",
            status: "received",
            errorMessage: "",
            createdAt: data.createdAt || null,
            tournamentId: data.tournamentId || null,
            requestedBySource: "member_reply",
            requestedByName: "",
            provider: "twilio",
            providerMessageId: data.messageSid || "",
            action: data.action || "",
          };
        });

        const combined = [...emailEntries, ...smsEntries, ...inboundSmsEntries].sort(
          (a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt)
        );

        setCommunicationHistory(combined);
      } catch (err) {
        console.error("Failed loading related admin detail data:", err);
      } finally {
        setLoadingRelated(false);
      }
    };

    loadRelated();
  }, [memberId, user]);

  useEffect(() => {
    setCommunicationFilter("all");
    setVisibleCommCount(COMM_PAGE_SIZE);
  }, [memberId]);

  useEffect(() => {
    setVisibleCommCount(COMM_PAGE_SIZE);
  }, [communicationFilter]);

  const handleToggleAdmin = async () => {
    if (!user) return;

    const removingAdmin = user.role === "admin";
    if (removingAdmin && adminCount <= 1) {
      return alert("You can’t remove the last admin.");
    }

    const nextRole = removingAdmin ? "member" : "admin";

    try {
      await updateDoc(doc(db, "users", user.id), {
        role: nextRole,
        updatedAt: serverTimestamp(),
      });

      setUser((prev) => ({
        ...prev,
        role: nextRole,
        updatedAt: new Date(),
      }));

      setAdminCount((prev) => (removingAdmin ? prev - 1 : prev + 1));
    } catch (err) {
      console.error("Role update failed:", err);
      alert("Could not update role.");
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    const nextStatus = isDisabled(user) ? "active" : "disabled";

    try {
      await updateDoc(doc(db, "users", user.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });

      setUser((prev) => ({
        ...prev,
        status: nextStatus,
        updatedAt: new Date(),
      }));
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Could not update status.");
    }
  };

  const handleSendSetupEmail = async () => {
    if (!user?.email) {
      return alert("User has no email.");
    }

    const confirmSend = window.confirm(
      `Email a new temporary password to ${user.email}?`
    );
    if (!confirmSend) return;

    setSendingEmail(true);

    try {
      await sendCustomPasswordSetupEmail({
        email: String(user.email).trim().toLowerCase(),
      });

      await refreshUserDoc();
      setShowStoredTempPassword(true);

      alert(
        "Temporary password email sent. The current temporary password is stored on this admin page until the member changes it."
      );
    } catch (err) {
      console.error("Temporary password email failed:", err);
      alert("Failed to send temporary password email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSetTemporaryPassword = async () => {
    if (!user?.id) return;

    const input = window.prompt(
      [
        `Set a temporary password for ${user.name || user.email}?`,
        "",
        "Leave blank and press OK to auto-generate one.",
        "Or type a temporary password manually.",
        "",
        "Temporary passwords must be at least 8 characters.",
      ].join("\n")
    );

    if (input === null) return;

    const manualPassword = String(input || "").trim();

    if (manualPassword && manualPassword.length < 8) {
      alert("Temporary password must be at least 8 characters.");
      return;
    }

    const confirmMessage = manualPassword
      ? `Set the manual temporary password for ${user.name || user.email}?`
      : `Auto-generate a temporary password for ${user.name || user.email}?`;

    const ok = window.confirm(confirmMessage);
    if (!ok) return;

    setSettingTemporaryPassword(true);
    setTempPasswordResult(null);

    try {
      const result = await adminSetTemporaryPassword({
        uid: user.id,
        temporaryPassword: manualPassword || undefined,
      });

      const nextPassword = String(result?.data?.temporaryPassword || "");

      setTempPasswordResult({
        password: nextPassword,
        generated: !manualPassword,
      });

      setUser((prev) => ({
        ...prev,
        mustChangePassword: true,
        temporaryPasswordEnabled: true,
        adminVisibleTempPassword: nextPassword,
        tempPasswordSetAt: new Date(),
        updatedAt: new Date(),
      }));

      setShowStoredTempPassword(true);

      alert(
        "Temporary password set successfully. It is now visible on this admin page and will be cleared automatically after the member resets their password."
      );
    } catch (err) {
      console.error("Set temporary password failed:", err);
      alert(err?.message || "Failed to set temporary password.");
    } finally {
      setSettingTemporaryPassword(false);
    }
  };

  const handleCopyTemporaryPassword = async () => {
    const passwordToCopy = String(
      tempPasswordResult?.password || user?.adminVisibleTempPassword || ""
    ).trim();

    if (!passwordToCopy) return;

    try {
      setCopyingTempPassword(true);
      await navigator.clipboard.writeText(passwordToCopy);
      alert("Temporary password copied.");
    } catch (err) {
      console.error("Copy temporary password failed:", err);
      alert("Could not copy password. Please copy it manually.");
    } finally {
      setCopyingTempPassword(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!user) return;

    setSavingNotes(true);

    try {
      await updateDoc(doc(db, "users", user.id), {
        adminNotes: String(notesDraft || ""),
        adminNotesUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setUser((prev) => ({
        ...prev,
        adminNotes: String(notesDraft || ""),
        adminNotesUpdatedAt: new Date(),
        updatedAt: new Date(),
      }));

      alert("Admin notes saved.");
    } catch (err) {
      console.error("Saving notes failed:", err);
      alert("Failed to save admin notes.");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    const payload = {
      uid: user.id,
      name: String(profileForm.name || "").trim(),
      email: String(profileForm.email || "").trim(),
      phone: String(profileForm.phone || "").trim(),
    };

    if (!payload.email) {
      return alert("Email is required.");
    }

    setSavingProfile(true);

    try {
      const result = await adminUpdateUserProfile(payload);
      const nextUser = result?.data?.user || {};

      setUser((prev) => ({
        ...prev,
        name: nextUser.name ?? payload.name,
        email: nextUser.email ?? payload.email.toLowerCase(),
        phone: nextUser.phone ?? payload.phone,
        emailLower: normLower(nextUser.email ?? payload.email),
        nameLower: normLower(
          (nextUser.name ?? payload.name) || (nextUser.email ?? payload.email)
        ),
        updatedAt: new Date(),
      }));

      setProfileForm({
        name: nextUser.name ?? payload.name,
        email: nextUser.email ?? payload.email.toLowerCase(),
        phone: nextUser.phone ?? payload.phone,
      });

      alert("Profile updated.");
    } catch (err) {
      console.error("Profile update failed:", err);
      alert(err?.message || "Failed to update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleVerification = async (type) => {
    if (!user) return;

    const field = type === "email" ? "emailVerified" : "phoneVerified";
    const atField = type === "email" ? "emailVerifiedAt" : "phoneVerifiedAt";
    const nextValue = !Boolean(user[field]);

    if (type === "email") {
      setTogglingEmailVerification(true);
    } else {
      setTogglingPhoneVerification(true);
    }

    try {
      await updateDoc(doc(db, "users", user.id), {
        [field]: nextValue,
        [atField]: nextValue ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      });

      setUser((prev) => ({
        ...prev,
        [field]: nextValue,
        [atField]: nextValue ? new Date() : null,
        updatedAt: new Date(),
      }));
    } catch (err) {
      console.error("Verification toggle failed:", err);
      alert(`Failed to update ${type} verification.`);
    } finally {
      if (type === "email") {
        setTogglingEmailVerification(false);
      } else {
        setTogglingPhoneVerification(false);
      }
    }
  };

  const summaryStats = useMemo(() => {
    const total = tournamentHistory.length;
    const wins = tournamentHistory.filter((t) => t.entrant?.winner).length;
    const earlyBirds = tournamentHistory.filter((t) => t.entrant?.earlyBirdQualified).length;
    const rebuys = tournamentHistory.reduce(
      (sum, t) => sum + Number(t.entrant?.rebuys || 0),
      0
    );

    return { total, wins, earlyBirds, rebuys };
  }, [tournamentHistory]);

  const latestEvent = useMemo(() => {
    if (!Array.isArray(tournamentHistory) || tournamentHistory.length === 0) return null;

    return tournamentHistory[0];
  }, [tournamentHistory]);

  const filteredCommunicationHistory = useMemo(() => {
    const all = Array.isArray(communicationHistory) ? communicationHistory : [];

    switch (communicationFilter) {
      case "email":
        return all.filter((entry) => entry.channel === "email");
      case "sms":
        return all.filter((entry) => entry.channel === "sms");
      case "inbound":
        return all.filter((entry) => entry.channel === "sms_inbound");
      case "failed":
        return all.filter(
          (entry) =>
            entry.channel !== "sms_inbound" &&
            (normLower(entry.status) === "failed" || !!entry.errorMessage)
        );
      default:
        return all;
    }
  }, [communicationHistory, communicationFilter]);

  const visibleCommunicationHistory = useMemo(() => {
    return filteredCommunicationHistory.slice(0, visibleCommCount);
  }, [filteredCommunicationHistory, visibleCommCount]);

  const hasMoreCommunication = visibleCommCount < filteredCommunicationHistory.length;

  if (loading) {
    return (
      <Layout>
        <p className="text-zinc-300">Loading member details...</p>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="space-y-4">
          <button
            onClick={() => navigate("/admin/users")}
            className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
          >
            ← Back to Admin Users
          </button>
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-zinc-300">
            Member not found.
          </div>
        </div>
      </Layout>
    );
  }

  const disabled = isDisabled(user);
  const emailVerified = user?.emailVerified === true;
  const phoneVerified = user?.phoneVerified === true;
  const joinedAtText = formatDateTime(getJoinedAt(user));
  const mustChangePassword = user?.mustChangePassword === true;
  const temporaryPasswordEnabled = user?.temporaryPasswordEnabled === true;
  const storedTempPassword = String(user?.adminVisibleTempPassword || "").trim();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Avatar user={user} size={72} />

            <div>
              <button
                onClick={() => navigate("/admin/users")}
                className="text-zinc-400 hover:text-zinc-200 text-sm mb-2"
              >
                ← Back to Admin Users
              </button>

              <h2 className="text-3xl font-bold text-[#B87333]">
                {user.name || user.displayName || "Member Details"}
              </h2>

              <p className="text-zinc-400 mt-1 break-all">{user.email || "—"}</p>

              <div className="mt-3 flex flex-wrap gap-2">
                <Badge
                  text={user.role === "admin" ? "Admin" : "Member"}
                  tone={user.role === "admin" ? "copper" : "zinc"}
                />
                <Badge
                  text={disabled ? "Disabled" : "Active"}
                  tone={disabled ? "red" : "green"}
                />
                <Badge
                  text={user.smsOptIn ? "SMS Opted In" : "SMS Off"}
                  tone={user.smsOptIn ? "blue" : "zinc"}
                />
                <Badge
                  text={emailVerified ? "Email Verified" : "Email Unverified"}
                  tone={emailVerified ? "green" : "amber"}
                />
                <Badge
                  text={phoneVerified ? "Phone Verified" : "Phone Unverified"}
                  tone={phoneVerified ? "green" : "amber"}
                />
                {temporaryPasswordEnabled ? (
                  <Badge text="Temp Password Active" tone="amber" />
                ) : null}
                {mustChangePassword ? (
                  <Badge text="Must Change Password" tone="red" />
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleToggleAdmin}
              className={`px-4 py-3 rounded font-semibold ${
                user.role === "admin" && adminCount <= 1
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
              }`}
              disabled={user.role === "admin" && adminCount <= 1}
              title={
                user.role === "admin" && adminCount <= 1
                  ? "You can’t remove the last admin."
                  : undefined
              }
            >
              {user.role === "admin" ? "Remove Admin" : "Make Admin"}
            </button>

            <button
              onClick={handleToggleStatus}
              className={`px-4 py-3 rounded font-semibold ${
                disabled
                  ? "bg-green-700 hover:bg-green-800 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              {disabled ? "Activate User" : "Disable User"}
            </button>

            <button
              onClick={handleSendSetupEmail}
              className="px-4 py-3 rounded bg-zinc-700 hover:bg-zinc-600 text-white font-semibold"
              disabled={sendingEmail}
            >
              {sendingEmail ? "Sending..." : "Email Temporary Password"}
            </button>

            <button
              onClick={handleSetTemporaryPassword}
              className="px-4 py-3 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              disabled={settingTemporaryPassword}
            >
              {settingTemporaryPassword ? "Setting..." : "Set Temporary Password"}
            </button>
          </div>
        </div>

        {tempPasswordResult?.password ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-amber-300">
                  Temporary Password Created
                </h3>
                <p className="text-sm text-amber-100/90 mt-1">
                  This temporary password is also stored on the admin page below
                  and will be cleared automatically after the member resets their password.
                </p>
                <div className="mt-3 inline-flex items-center rounded-lg bg-zinc-950 border border-zinc-800 px-4 py-3 text-zinc-100 font-mono text-sm break-all">
                  {tempPasswordResult.password}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleCopyTemporaryPassword}
                  className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
                  disabled={copyingTempPassword}
                >
                  {copyingTempPassword ? "Copying..." : "Copy Password"}
                </button>

                <button
                  onClick={() => setTempPasswordResult(null)}
                  className="px-4 py-2 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 font-semibold border border-zinc-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <TinyStat label="Joined" value={formatDateOnly(getJoinedAt(user))} />
          <TinyStat label="Events" value={summaryStats.total} />
          <TinyStat label="Wins" value={summaryStats.wins} />
          <TinyStat label="Rebuys" value={summaryStats.rebuys} />
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <DetailCard
            title="Editable Profile"
            right={
              <button
                onClick={handleSaveProfile}
                className="px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
                disabled={savingProfile}
              >
                {savingProfile ? "Saving..." : "Save Profile"}
              </button>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Full Name</label>
                <input
                  className="w-full px-3 py-3 rounded bg-zinc-950 border border-zinc-800 text-zinc-100"
                  value={profileForm.name}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Member name"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-1">Email</label>
                <input
                  className="w-full px-3 py-3 rounded bg-zinc-950 border border-zinc-800 text-zinc-100"
                  value={profileForm.email}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="member@email.com"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-1">Phone</label>
                <input
                  className="w-full px-3 py-3 rounded bg-zinc-950 border border-zinc-800 text-zinc-100"
                  value={profileForm.phone}
                  onChange={(e) =>
                    setProfileForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="9795551234"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Save profile updates name, email, and phone in the user record and
                  Auth email/display name.
                </p>
              </div>
            </div>
          </DetailCard>

          <DetailCard title="Account">
            <Row label="Full Name" value={user.name || user.displayName || "—"} />
            <Row label="Email" value={user.email || "—"} />
            <Row label="Phone" value={user.phone || "—"} />
            <Row label="Role" value={user.role || "member"} />
            <Row label="Status" value={disabled ? "disabled" : "active"} />
            <Row label="User ID" value={user.id} />
          </DetailCard>

          <DetailCard title="Membership & Activity">
            <Row label="Joined / Approved" value={joinedAtText} />
            <Row
              label="Last Event"
              value={
                latestEvent
                  ? `${formatDateTime(latestEvent.tournamentDate)}`
                  : "—"
              }
            />
            <Row
              label="Last Event Name"
              value={latestEvent?.tournamentName || "—"}
            />
            <Row
              label="Last Event Status"
              value={latestEvent ? buildTournamentHistoryStatus(latestEvent.entrant) : "—"}
            />
            <Row label="Created At" value={formatDateTime(user.createdAt)} />
            <Row label="Updated At" value={formatDateTime(user.updatedAt)} />
            <Row label="Approved At" value={formatDateTime(user.approvedAt)} />
          </DetailCard>

          <DetailCard
            title="Verification & Communications"
            right={
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleToggleVerification("email")}
                  className={`px-3 py-2 rounded text-sm font-semibold ${
                    emailVerified
                      ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "bg-green-700 hover:bg-green-800 text-white"
                  }`}
                  disabled={togglingEmailVerification}
                >
                  {togglingEmailVerification
                    ? "Saving..."
                    : emailVerified
                    ? "Mark Email Unverified"
                    : "Mark Email Verified"}
                </button>

                <button
                  onClick={() => handleToggleVerification("phone")}
                  className={`px-3 py-2 rounded text-sm font-semibold ${
                    phoneVerified
                      ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "bg-green-700 hover:bg-green-800 text-white"
                  }`}
                  disabled={togglingPhoneVerification}
                >
                  {togglingPhoneVerification
                    ? "Saving..."
                    : phoneVerified
                    ? "Mark Phone Unverified"
                    : "Mark Phone Verified"}
                </button>
              </div>
            }
          >
            <Row label="Email Verified" value={emailVerified ? "Yes" : "No"} />
            <Row label="Email Verified At" value={formatDateTime(user.emailVerifiedAt)} />
            <Row label="Phone Verified" value={phoneVerified ? "Yes" : "No"} />
            <Row label="Phone Verified At" value={formatDateTime(user.phoneVerifiedAt)} />
            <Row label="SMS Opt-In" value={user.smsOptIn ? "Yes" : "No"} />
            <Row label="SMS Opt-In At" value={formatDateTime(user.smsOptInAt)} />
            <Row
              label="Agreement Accepted At"
              value={formatDateTime(user.agreementAcceptedAt)}
            />
            <Row label="Agreement Version" value={user.agreementVersion || "—"} />
          </DetailCard>

          <DetailCard title="Password Access">
            <Row label="Must Change Password" value={mustChangePassword ? "Yes" : "No"} />
            <Row
              label="Temporary Password Active"
              value={temporaryPasswordEnabled ? "Yes" : "No"}
            />
            <Row label="Temporary Password Set At" value={formatDateTime(user.tempPasswordSetAt)} />
            <Row
              label="Temporary Password Set By UID"
              value={user.tempPasswordSetByUid || "—"}
            />
            <Row
              label="Temporary Password Email Last Sent"
              value={formatDateTime(user.tempPasswordEmailLastSentAt)}
            />
            <Row
              label="Temporary Password Cleared At"
              value={formatDateTime(user.temporaryPasswordClearedAt)}
            />
            <Row label="Password Changed At" value={formatDateTime(user.passwordChangedAt)} />

            <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Current Stored Temporary Password
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Visible to admins only. Cleared automatically after the member resets their password.
                  </div>
                </div>

                {storedTempPassword ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setShowStoredTempPassword((prev) => !prev)}
                      className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
                    >
                      {showStoredTempPassword ? "Hide" : "Reveal"}
                    </button>

                    <button
                      type="button"
                      onClick={handleCopyTemporaryPassword}
                      className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
                      disabled={copyingTempPassword}
                    >
                      {copyingTempPassword ? "Copying..." : "Copy"}
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="mt-3 rounded-lg border border-zinc-800 bg-black/20 px-4 py-3 font-mono text-sm text-zinc-100 break-all">
                {storedTempPassword
                  ? showStoredTempPassword
                    ? storedTempPassword
                    : "••••••••••••"
                  : "No active temporary password stored."}
              </div>
            </div>

            <p className="text-xs text-zinc-500 mt-4">
              Use temporary password only as a fallback when setup emails are delayed or
              expired. Members will be forced to change it after logging in.
            </p>
          </DetailCard>

          <DetailCard title="Original Application Details">
            {loadingRelated ? (
              <p className="text-zinc-400 text-sm">Loading application details...</p>
            ) : application ? (
              <>
                <Row label="Application ID" value={application.id} />
                <Row label="Application Status" value={application.status || "—"} />
                <Row label="Submitted At" value={formatDateTime(application.submittedAt)} />
                <Row label="Approved At" value={formatDateTime(application.approvedAt)} />
                <Row label="Applicant Name" value={application.name || "—"} />
                <Row label="Applicant Email" value={application.email || "—"} />
                <Row label="Applicant Phone" value={application.phone || "—"} />
                <Row label="Date of Birth" value={application.dob || "—"} />
                <Row
                  label="Agreement Accepted"
                  value={application.agreementAccepted ? "Yes" : "No"}
                />
                <Row
                  label="Agreement Accepted At"
                  value={formatDateTime(application.agreementAcceptedAt)}
                />
                <Row label="Agreement Version" value={application.agreementVersion || "—"} />
                <Row label="SMS Opt-In at Apply" value={application.smsOptIn ? "Yes" : "No"} />
                <Row label="SMS Opt-In At" value={formatDateTime(application.smsOptInAt)} />
              </>
            ) : (
              <p className="text-zinc-400 text-sm">No matching application record found.</p>
            )}
          </DetailCard>

          <DetailCard title="Internal Admin Notes">
            <div className="space-y-3">
              <textarea
                className="w-full min-h-[180px] rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-zinc-100 outline-none focus:border-[#B87333]"
                placeholder="Add private admin notes about this member..."
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
              />

              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-zinc-500">
                  Last updated: {formatDateTime(user.adminNotesUpdatedAt)}
                </p>

                <button
                  onClick={handleSaveNotes}
                  className="px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
                  disabled={savingNotes}
                >
                  {savingNotes ? "Saving..." : "Save Notes"}
                </button>
              </div>
            </div>
          </DetailCard>

          <DetailCard title="Tournament History">
            {loadingRelated ? (
              <p className="text-zinc-400 text-sm">Loading tournament history...</p>
            ) : tournamentHistory.length === 0 ? (
              <p className="text-zinc-400 text-sm">
                No tournament history found for this member.
              </p>
            ) : (
              <div className="space-y-3">
                {tournamentHistory.map((item) => {
                  const statusText = buildTournamentHistoryStatus(item.entrant);

                  return (
                    <button
                      key={item.tournamentId}
                      type="button"
                      onClick={() => navigate(`/tournament/${item.tournamentId}`)}
                      className="w-full text-left rounded-lg border border-zinc-800 bg-zinc-950 p-4 hover:border-[#B87333]/40"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                        <div>
                          <div className="text-zinc-100 font-semibold">
                            {item.tournamentName}
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            {formatDateTime(item.tournamentDate)}
                          </div>
                          <div className="text-sm text-zinc-500 mt-1">
                            {item.tournamentLocation || "Location TBD"}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Badge
                            text={statusText}
                            tone={item.entrant?.winner ? "copper" : "blue"}
                          />
                          <Badge text={`Buy-In $${Number(item.buyIn || 0)}`} tone="zinc" />
                          {item.entrant?.earlyBirdQualified ? (
                            <Badge text="Early Bird" tone="green" />
                          ) : null}
                          {Number(item.entrant?.rebuys || 0) > 0 ? (
                            <Badge text={`Rebuys ${item.entrant.rebuys}`} tone="amber" />
                          ) : null}
                          {item.entrant?.addOnTaken ? (
                            <Badge text="Add-On Taken" tone="green" />
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </DetailCard>
        </div>

        <DetailCard
          title="Communication History"
          right={
            !loadingRelated && communicationHistory.length > 0 ? (
              <div className="text-xs text-zinc-500">
                Showing {visibleCommunicationHistory.length} of {filteredCommunicationHistory.length}
                {communicationFilter !== "all" ? ` filtered` : ""}
              </div>
            ) : null
          }
        >
          {loadingRelated ? (
            <p className="text-zinc-400 text-sm">Loading communication history...</p>
          ) : communicationHistory.length === 0 ? (
            <p className="text-zinc-400 text-sm">
              No communication history found for this member yet.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <FilterPill
                  label={`All (${communicationHistory.length})`}
                  active={communicationFilter === "all"}
                  onClick={() => setCommunicationFilter("all")}
                />
                <FilterPill
                  label={`Email (${communicationHistory.filter((e) => e.channel === "email").length})`}
                  active={communicationFilter === "email"}
                  onClick={() => setCommunicationFilter("email")}
                />
                <FilterPill
                  label={`SMS (${communicationHistory.filter((e) => e.channel === "sms").length})`}
                  active={communicationFilter === "sms"}
                  onClick={() => setCommunicationFilter("sms")}
                />
                <FilterPill
                  label={`Inbound (${communicationHistory.filter((e) => e.channel === "sms_inbound").length})`}
                  active={communicationFilter === "inbound"}
                  onClick={() => setCommunicationFilter("inbound")}
                />
                <FilterPill
                  label={`Failed (${
                    communicationHistory.filter(
                      (e) =>
                        e.channel !== "sms_inbound" &&
                        (normLower(e.status) === "failed" || !!e.errorMessage)
                    ).length
                  })`}
                  active={communicationFilter === "failed"}
                  onClick={() => setCommunicationFilter("failed")}
                />
              </div>

              {filteredCommunicationHistory.length === 0 ? (
                <p className="text-zinc-400 text-sm">
                  No communication matches this filter.
                </p>
              ) : (
                <>
                  <div className="space-y-3">
                    {visibleCommunicationHistory.map((entry) => {
                      const statusMeta = getCommunicationStatusMeta(entry);
                      const sourceLabel = prettifySource(entry.requestedBySource);
                      const directionLabel = communicationDirectionLabel(entry);

                      return (
                        <div
                          key={`${entry.channel}-${entry.id}`}
                          className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge
                                  text={
                                    entry.channel === "email"
                                      ? "Email"
                                      : entry.channel === "sms"
                                      ? "SMS"
                                      : "Inbound SMS"
                                  }
                                  tone={
                                    entry.channel === "email"
                                      ? "blue"
                                      : entry.channel === "sms"
                                      ? "copper"
                                      : "amber"
                                  }
                                />
                                <Badge text={communicationTypeLabel(entry)} tone="zinc" />
                                <Badge text={statusMeta.text} tone={statusMeta.tone} />
                              </div>

                              <div className="text-zinc-100 font-semibold mt-3 break-words">
                                {entryHeading(entry)}
                              </div>

                              <div className="text-sm text-zinc-400 mt-1 break-all">
                                {directionLabel}: {entry.to || "—"}
                              </div>

                              {entry.preview ? (
                                <div className="text-sm text-zinc-500 mt-2 whitespace-pre-wrap break-words">
                                  {entry.preview.length > 220
                                    ? `${entry.preview.slice(0, 220)}…`
                                    : entry.preview}
                                </div>
                              ) : null}

                              {entry.action ? (
                                <div className="text-xs text-zinc-500 mt-2">
                                  Action: {prettifyInboundAction(entry.action)}
                                </div>
                              ) : null}

                              {sourceLabel ? (
                                <div className="text-xs text-zinc-500 mt-2">
                                  Source: {sourceLabel}
                                </div>
                              ) : null}

                              {entry.requestedByName ? (
                                <div className="text-xs text-zinc-500 mt-1">
                                  Sent by: {entry.requestedByName}
                                </div>
                              ) : null}

                              {entry.provider ? (
                                <div className="text-xs text-zinc-500 mt-1">
                                  Provider: {entry.provider}
                                  {entry.providerMessageId
                                    ? ` • ID: ${entry.providerMessageId}`
                                    : ""}
                                </div>
                              ) : null}

                              {entry.errorMessage ? (
                                <div className="text-sm text-red-300 mt-2 break-words">
                                  Error: {entry.errorMessage}
                                </div>
                              ) : null}
                            </div>

                            <div className="flex flex-col items-start lg:items-end gap-2">
                              <div className="text-xs text-zinc-500">
                                {formatDateTime(entry.createdAt)}
                              </div>

                              {entry.tournamentId ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/tournament/${entry.tournamentId}`)}
                                  className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
                                >
                                  Open Tournament
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasMoreCommunication ? (
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleCommCount((prev) => prev + COMM_PAGE_SIZE)
                        }
                        className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
                      >
                        Load More
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          )}
        </DetailCard>
      </div>
    </Layout>
  );
}